export const WEFAT_STORAGE_API_NAMES = Object.freeze([
    'setStorage',
    'setStorageSync',
    'getStorage',
    'getStorageSync',
    'removeStorage',
    'removeStorageSync',
    'clearStorage',
    'clearStorageSync',
    'getStorageInfo',
    'getStorageInfoSync',
]);
const DEFAULT_NAMESPACE = 'default';
const DEFAULT_LIMIT_BYTES = 10 * 1024 * 1024;
const DEFAULT_PER_KEY_LIMIT_BYTES = 1024 * 1024;
const MAX_STORAGE_DEPTH = 100;
const STORAGE_SCHEMA_VERSION = 1;
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const SET_OPTIONS = new Set([...CALLBACK_KEYS, 'key', 'data', 'encrypt']);
const GET_OPTIONS = new Set([...CALLBACK_KEYS, 'key', 'encrypt']);
const REMOVE_OPTIONS = new Set([...CALLBACK_KEYS, 'key']);
const CALLBACK_OPTIONS = CALLBACK_KEYS;
export class WxStorageRuntimeError extends Error {
    code;
    errCode;
    errMsg;
    api;
    remediation;
    constructor(code, api, reason, remediation, cause) {
        const errMsg = `${api}:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxStorageRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.api = api;
        this.remediation = remediation;
        if (cause !== undefined) {
            Object.defineProperty(this, 'cause', {
                configurable: false,
                enumerable: false,
                value: cause,
                writable: false,
            });
        }
    }
}
function invalidOption(api, reason) {
    return new WxStorageRuntimeError('WF6110', api, reason, 'Pass the documented storage option object with a non-empty string key and callable callbacks.');
}
function unsupportedData(api, path, reason) {
    return new WxStorageRuntimeError('WF6111', api, `storage data at ${path} ${reason}`, 'Store finite JSON-like values, valid Date instances, plain objects, and dense arrays without accessors, cycles, or unsafe keys.');
}
function storageUnavailable(api, operation, cause) {
    return new WxStorageRuntimeError('WF6112', api, `browser storage ${operation} failed`, 'Use an available Web Storage implementation and allow storage access for this origin.', cause);
}
function missingEntry(api, key) {
    return new WxStorageRuntimeError('WF6113', api, `data not found for key ${JSON.stringify(key)}`, 'Store the key before reading it, or handle the fail callback or rejected Promise.');
}
function quotaExceeded(api, reason, cause) {
    return new WxStorageRuntimeError('WF6114', api, reason, 'Remove unneeded Wefat storage entries or reduce the serialized value below the configured per-key and total limits.', cause);
}
function encryptionUnavailable(api) {
    return new WxStorageRuntimeError('WF6115', api, 'encrypt is not available in the generic browser storage adapter', 'Configure a reviewed encryption provider, or call the browser adapter without encrypt: true.');
}
function corruptEntry(api, reason, cause) {
    return new WxStorageRuntimeError('WF6116', api, `stored data is invalid: ${reason}`, 'Remove the damaged namespaced entry and write it again through the Wefat storage API.', cause);
}
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function assertApiOptions(api, value, allowedKeys) {
    if (!isPlainRecord(value)) {
        throw invalidOption(api, 'options must be a plain object');
    }
    for (const key of Reflect.ownKeys(value)) {
        if (typeof key !== 'string' || !allowedKeys.has(key)) {
            throw invalidOption(api, `unsupported option field ${String(key)}`);
        }
        const descriptor = Object.getOwnPropertyDescriptor(value, key);
        if (descriptor === undefined || !('value' in descriptor)) {
            throw invalidOption(api, `option field ${key} must be a direct data property`);
        }
    }
    return value;
}
function dataProperty(api, options, key, required) {
    const descriptor = Object.getOwnPropertyDescriptor(options, key);
    if (descriptor === undefined) {
        if (required) {
            throw invalidOption(api, `missing required option field ${key}`);
        }
        return undefined;
    }
    if (!('value' in descriptor)) {
        throw invalidOption(api, `option field ${key} must not be an accessor`);
    }
    return descriptor.value;
}
function storageKey(api, options) {
    const key = dataProperty(api, options, 'key', true);
    if (typeof key !== 'string' || key.length === 0) {
        throw invalidOption(api, 'key must be a non-empty string');
    }
    return key;
}
function assertEncryption(api, options) {
    const encrypt = dataProperty(api, options, 'encrypt', false);
    if (encrypt !== undefined && typeof encrypt !== 'boolean') {
        throw invalidOption(api, 'encrypt must be a boolean when provided');
    }
    if (encrypt === true) {
        throw encryptionUnavailable(api);
    }
}
function callbackProperty(api, options, key) {
    const value = dataProperty(api, options, key, false);
    if (value !== undefined && typeof value !== 'function') {
        throw invalidOption(api, `${key} must be a function when provided`);
    }
    return value;
}
function readCallbacks(api, rawOptions) {
    if (!isPlainRecord(rawOptions)) {
        return { success: undefined, fail: undefined, complete: undefined, used: false };
    }
    const success = callbackProperty(api, rawOptions, 'success');
    const fail = callbackProperty(api, rawOptions, 'fail');
    const complete = callbackProperty(api, rawOptions, 'complete');
    return {
        success,
        fail,
        complete,
        used: success !== undefined || fail !== undefined || complete !== undefined,
    };
}
function isArrayIndex(key, length) {
    if (key.length === 0 || [...key].some((character) => character < '0' || character > '9')) {
        return false;
    }
    const index = Number(key);
    return Number.isSafeInteger(index) && index >= 0 && index < length && String(index) === key;
}
function encodeStorageValue(api, value, path, ancestors, depth) {
    if (depth > MAX_STORAGE_DEPTH) {
        throw unsupportedData(api, path, `exceeds the maximum depth of ${MAX_STORAGE_DEPTH}`);
    }
    if (value === null) {
        return { type: 'null' };
    }
    if (typeof value === 'boolean') {
        return { type: 'boolean', value };
    }
    if (typeof value === 'string') {
        return { type: 'string', value };
    }
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) {
            throw unsupportedData(api, path, 'contains a non-finite number');
        }
        return { type: 'number', value };
    }
    if (value instanceof Date) {
        const time = Date.prototype.getTime.call(value);
        if (!Number.isFinite(time)) {
            throw unsupportedData(api, path, 'contains an invalid Date');
        }
        return { type: 'date', value: time };
    }
    if (typeof value !== 'object') {
        throw unsupportedData(api, path, `contains an unsupported ${typeof value} value`);
    }
    if (ancestors.has(value)) {
        throw unsupportedData(api, path, 'contains a circular reference');
    }
    ancestors.add(value);
    try {
        if (Array.isArray(value)) {
            for (const key of Reflect.ownKeys(value)) {
                if (key === 'length') {
                    continue;
                }
                if (typeof key !== 'string' || !isArrayIndex(key, value.length)) {
                    throw unsupportedData(api, path, `contains an unsupported array property ${String(key)}`);
                }
            }
            const encoded = [];
            for (let index = 0; index < value.length; index += 1) {
                const descriptor = Object.getOwnPropertyDescriptor(value, index);
                if (descriptor === undefined) {
                    throw unsupportedData(api, `${path}[${index}]`, 'is a sparse array entry');
                }
                if (!('value' in descriptor)) {
                    throw unsupportedData(api, `${path}[${index}]`, 'is an accessor property');
                }
                encoded.push(descriptor.value === undefined
                    ? { type: 'null' }
                    : encodeStorageValue(api, descriptor.value, `${path}[${index}]`, ancestors, depth + 1));
            }
            return { type: 'array', value: encoded };
        }
        if (!isPlainRecord(value)) {
            throw unsupportedData(api, path, 'is not a plain object');
        }
        const encoded = Object.create(null);
        for (const key of Reflect.ownKeys(value)) {
            if (typeof key !== 'string') {
                throw unsupportedData(api, path, 'contains a symbol property');
            }
            if (UNSAFE_KEYS.has(key)) {
                throw unsupportedData(api, path, `contains the unsafe key ${key}`);
            }
            const descriptor = Object.getOwnPropertyDescriptor(value, key);
            if (descriptor === undefined || !('value' in descriptor)) {
                throw unsupportedData(api, `${path}.${key}`, 'is an accessor property');
            }
            if (descriptor.enumerable !== true) {
                throw unsupportedData(api, `${path}.${key}`, 'is a non-enumerable property');
            }
            if (descriptor.value === undefined) {
                continue;
            }
            encoded[key] = encodeStorageValue(api, descriptor.value, `${path}.${key}`, ancestors, depth + 1);
        }
        return { type: 'object', value: encoded };
    }
    finally {
        ancestors.delete(value);
    }
}
function serializeStorageValue(api, value) {
    const envelope = {
        schemaVersion: STORAGE_SCHEMA_VERSION,
        value: encodeStorageValue(api, value, '$', new Set(), 0),
    };
    return JSON.stringify(envelope);
}
function exactKeys(value, expected) {
    const keys = Object.keys(value).sort();
    const expectedKeys = [...expected].sort();
    return keys.length === expectedKeys.length && keys.every((key, index) => key === expectedKeys[index]);
}
function decodeStorageValue(api, value, path, depth) {
    if (depth > MAX_STORAGE_DEPTH || !isPlainRecord(value) || typeof value.type !== 'string') {
        throw corruptEntry(api, `${path} has an invalid encoded shape`);
    }
    if (value.type === 'null' && exactKeys(value, ['type'])) {
        return null;
    }
    if (value.type === 'boolean' && exactKeys(value, ['type', 'value']) && typeof value.value === 'boolean') {
        return value.value;
    }
    if (value.type === 'string' && exactKeys(value, ['type', 'value']) && typeof value.value === 'string') {
        return value.value;
    }
    if (value.type === 'number' && exactKeys(value, ['type', 'value']) && typeof value.value === 'number' && Number.isFinite(value.value)) {
        return value.value;
    }
    if (value.type === 'date' && exactKeys(value, ['type', 'value']) && typeof value.value === 'number' && Number.isFinite(value.value)) {
        const date = new Date(value.value);
        if (Number.isFinite(Date.prototype.getTime.call(date))) {
            return date;
        }
    }
    if (value.type === 'array' && exactKeys(value, ['type', 'value']) && Array.isArray(value.value)) {
        return value.value.map((item, index) => decodeStorageValue(api, item, `${path}[${index}]`, depth + 1));
    }
    if (value.type === 'object' && exactKeys(value, ['type', 'value']) && isPlainRecord(value.value)) {
        const decoded = {};
        for (const key of Object.keys(value.value)) {
            if (UNSAFE_KEYS.has(key)) {
                throw corruptEntry(api, `${path} contains the unsafe key ${key}`);
            }
            decoded[key] = decodeStorageValue(api, value.value[key], `${path}.${key}`, depth + 1);
        }
        return decoded;
    }
    throw corruptEntry(api, `${path} has an unknown or malformed encoded value`);
}
function deserializeStorageValue(api, serialized) {
    let parsed;
    try {
        parsed = JSON.parse(serialized);
    }
    catch (error) {
        throw corruptEntry(api, 'the serialized envelope is not valid JSON', error);
    }
    if (!isPlainRecord(parsed)
        || !exactKeys(parsed, ['schemaVersion', 'value'])
        || parsed.schemaVersion !== STORAGE_SCHEMA_VERSION) {
        throw corruptEntry(api, 'the serialized envelope version or shape is unsupported');
    }
    return decodeStorageValue(api, parsed.value, '$', 0);
}
function byteLength(value) {
    return new TextEncoder().encode(value).byteLength;
}
function validLimit(value, fallback, label) {
    const resolved = value ?? fallback;
    if (!Number.isSafeInteger(resolved) || resolved <= 0) {
        throw new TypeError(`${label} must be a positive safe integer byte count`);
    }
    return resolved;
}
function validNamespace(namespace) {
    const resolved = namespace ?? DEFAULT_NAMESPACE;
    if (typeof resolved !== 'string' || resolved.length === 0 || resolved.length > 128) {
        throw new TypeError('storage namespace must be a non-empty string of at most 128 characters');
    }
    try {
        return encodeURIComponent(resolved);
    }
    catch (error) {
        throw new TypeError('storage namespace must contain valid Unicode text', { cause: error });
    }
}
function isWebStorageLike(value) {
    return value !== null
        && typeof value === 'object'
        && typeof Reflect.get(value, 'key') === 'function'
        && typeof Reflect.get(value, 'getItem') === 'function'
        && typeof Reflect.get(value, 'setItem') === 'function'
        && typeof Reflect.get(value, 'removeItem') === 'function';
}
function browserQuotaError(error) {
    if (error === null || typeof error !== 'object') {
        return false;
    }
    const name = Reflect.get(error, 'name');
    const code = Reflect.get(error, 'code');
    return name === 'QuotaExceededError'
        || name === 'NS_ERROR_DOM_QUOTA_REACHED'
        || code === 22
        || code === 1014;
}
function normalizeStorageError(api, error) {
    return error instanceof WxStorageRuntimeError
        ? error
        : storageUnavailable(api, 'operation', error);
}
export function createWebStorageWxApis(options) {
    if (options === null || typeof options !== 'object' || !isWebStorageLike(options.storage)) {
        throw new TypeError('createWebStorageWxApis requires a WebStorageLike storage implementation');
    }
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const namespace = validNamespace(options.namespace);
    const limitBytes = validLimit(options.limitBytes, DEFAULT_LIMIT_BYTES, 'limitBytes');
    const perKeyLimitBytes = validLimit(options.perKeyLimitBytes, DEFAULT_PER_KEY_LIMIT_BYTES, 'perKeyLimitBytes');
    if (perKeyLimitBytes > limitBytes) {
        throw new TypeError('perKeyLimitBytes must not exceed limitBytes');
    }
    const prefix = `wefat:storage:v${STORAGE_SCHEMA_VERSION}:${namespace}:`;
    const storage = options.storage;
    function fullKey(key) {
        return `${prefix}${key}`;
    }
    function entries(api) {
        try {
            const length = storage.length;
            if (!Number.isSafeInteger(length) || length < 0) {
                throw new TypeError('Web Storage length is invalid');
            }
            const found = new Map();
            for (let index = 0; index < length; index += 1) {
                const currentKey = storage.key(index);
                if (currentKey === null || !currentKey.startsWith(prefix)) {
                    continue;
                }
                const value = storage.getItem(currentKey);
                if (value !== null) {
                    found.set(currentKey, {
                        key: currentKey.slice(prefix.length),
                        storageKey: currentKey,
                        value,
                    });
                }
            }
            return Object.freeze([...found.values()].sort((left, right) => left.key < right.key ? -1 : left.key > right.key ? 1 : 0));
        }
        catch (error) {
            if (error instanceof WxStorageRuntimeError) {
                throw error;
            }
            throw storageUnavailable(api, 'enumeration', error);
        }
    }
    function entryBytes(entry) {
        return byteLength(entry.storageKey) + byteLength(entry.value);
    }
    function info(api) {
        const currentEntries = entries(api);
        const totalBytes = currentEntries.reduce((total, entry) => total + entryBytes(entry), 0);
        return {
            keys: currentEntries.map((entry) => entry.key),
            currentSize: Math.ceil(totalBytes / 1024),
            limitSize: limitBytes / 1024,
        };
    }
    function read(api, key, missingValue) {
        let value;
        try {
            value = storage.getItem(fullKey(key));
        }
        catch (error) {
            throw storageUnavailable(api, 'read', error);
        }
        if (value === null) {
            if (api === 'getStorageSync') {
                return missingValue;
            }
            throw missingEntry(api, key);
        }
        return deserializeStorageValue(api, value);
    }
    function write(api, key, data) {
        const serialized = serializeStorageValue(api, data);
        const serializedBytes = byteLength(serialized);
        if (serializedBytes > perKeyLimitBytes) {
            throw quotaExceeded(api, `serialized value for key ${JSON.stringify(key)} exceeds the ${perKeyLimitBytes}-byte per-key limit`);
        }
        const targetStorageKey = fullKey(key);
        const currentEntries = entries(api);
        const existing = currentEntries.find((entry) => entry.storageKey === targetStorageKey);
        const currentBytes = currentEntries.reduce((total, entry) => total + entryBytes(entry), 0);
        const nextBytes = currentBytes
            - (existing === undefined ? 0 : entryBytes(existing))
            + byteLength(targetStorageKey)
            + serializedBytes;
        if (nextBytes > limitBytes) {
            throw quotaExceeded(api, `namespaced storage exceeds the ${limitBytes}-byte total limit`);
        }
        try {
            storage.setItem(targetStorageKey, serialized);
        }
        catch (error) {
            if (browserQuotaError(error)) {
                throw quotaExceeded(api, 'the browser rejected the write because its storage quota was exceeded', error);
            }
            throw storageUnavailable(api, 'write', error);
        }
    }
    function remove(api, key) {
        try {
            storage.removeItem(fullKey(key));
        }
        catch (error) {
            throw storageUnavailable(api, 'removal', error);
        }
    }
    function clear(api) {
        const currentEntries = entries(api);
        try {
            for (const entry of currentEntries) {
                storage.removeItem(entry.storageKey);
            }
        }
        catch (error) {
            throw storageUnavailable(api, 'clear', error);
        }
    }
    function reportCallbackError(error, api, callback) {
        if (options.onCallbackError !== undefined) {
            try {
                options.onCallbackError(error, api, callback);
                return;
            }
            catch (reporterError) {
                queueMicrotask(() => {
                    throw reporterError;
                });
                return;
            }
        }
        queueMicrotask(() => {
            throw error;
        });
    }
    function invokeCallback(callback, value, api, kind) {
        if (callback === undefined) {
            return;
        }
        try {
            callback(value);
        }
        catch (error) {
            reportCallbackError(error, api, kind);
        }
    }
    function runAsync(api, rawOptions, allowedKeys, operation) {
        let callbacks;
        try {
            callbacks = readCallbacks(api, rawOptions);
        }
        catch (error) {
            return Promise.reject(normalizeStorageError(api, error));
        }
        const task = Promise.resolve().then(() => {
            const validated = assertApiOptions(api, rawOptions, allowedKeys);
            return operation(validated);
        });
        if (!callbacks.used) {
            return task;
        }
        void task.then((result) => {
            invokeCallback(callbacks.success, result, api, 'success');
            invokeCallback(callbacks.complete, result, api, 'complete');
        }, (error) => {
            const failure = normalizeStorageError(api, error);
            invokeCallback(callbacks.fail, failure, api, 'fail');
            invokeCallback(callbacks.complete, failure, api, 'complete');
        });
        return undefined;
    }
    const apis = {
        setStorage(rawOptions) {
            return runAsync('setStorage', rawOptions, SET_OPTIONS, (validated) => {
                assertEncryption('setStorage', validated);
                write('setStorage', storageKey('setStorage', validated), dataProperty('setStorage', validated, 'data', true));
                return { errMsg: 'setStorage:ok' };
            });
        },
        setStorageSync(key, data) {
            const validated = assertApiOptions('setStorageSync', { key }, new Set(['key']));
            write('setStorageSync', storageKey('setStorageSync', validated), data);
        },
        getStorage(rawOptions) {
            return runAsync('getStorage', rawOptions, GET_OPTIONS, (validated) => {
                assertEncryption('getStorage', validated);
                const key = storageKey('getStorage', validated);
                return {
                    data: read('getStorage', key, ''),
                    errMsg: 'getStorage:ok',
                };
            });
        },
        getStorageSync(key) {
            const validated = assertApiOptions('getStorageSync', { key }, new Set(['key']));
            return read('getStorageSync', storageKey('getStorageSync', validated), '');
        },
        removeStorage(rawOptions) {
            return runAsync('removeStorage', rawOptions, REMOVE_OPTIONS, (validated) => {
                remove('removeStorage', storageKey('removeStorage', validated));
                return { errMsg: 'removeStorage:ok' };
            });
        },
        removeStorageSync(key) {
            const validated = assertApiOptions('removeStorageSync', { key }, new Set(['key']));
            remove('removeStorageSync', storageKey('removeStorageSync', validated));
        },
        clearStorage(rawOptions = {}) {
            return runAsync('clearStorage', rawOptions, CALLBACK_OPTIONS, () => {
                clear('clearStorage');
                return { errMsg: 'clearStorage:ok' };
            });
        },
        clearStorageSync() {
            clear('clearStorageSync');
        },
        getStorageInfo(rawOptions = {}) {
            return runAsync('getStorageInfo', rawOptions, CALLBACK_OPTIONS, () => ({
                ...info('getStorageInfo'),
                errMsg: 'getStorageInfo:ok',
            }));
        },
        getStorageInfoSync() {
            return info('getStorageInfoSync');
        },
    };
    return Object.freeze(apis);
}
//# sourceMappingURL=storage-adapter.js.map